import React, { useState, useEffect, useRef } from 'react';
import {
  MessageCircle,
  X,
  Send,
  Paperclip,
  Search,
  Edit,
  Trash2,
  Reply,
  Check,
  CheckCheck,
  Download,
  Lock,
  Shield,
  Phone,
  Video,
  Info,
  Copy,
  RefreshCw,
  Plus,
  ChevronDown,
  Hash,
  Webhook
} from 'lucide-react';
import { User } from '../../types';
import { ChatMessage as ChatMessageType, ChatChannel } from '../../types/chat';
import { getApiUrl } from '../../config/api';
import { format, isSameDay, isToday, isYesterday } from 'date-fns';
import { encryptMessage, decryptMessage } from '../../utils/encryption';
import { subscribeToPushNotifications, isSubscribedToPush } from '../../utils/pushNotifications';
import Avatar from '../common/Avatar';
import { toast } from '../../utils/toast';
import { readApiError } from '../common/apiError';

interface ChatWidgetProps {
  currentUser: User;
  onUnreadCountChange?: (count: number) => void;
}

const ChatWidget: React.FC<ChatWidgetProps> = ({ currentUser, onUnreadCountChange }) => {
  const [channels, setChannels] = useState<ChatChannel[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [selectedChannel, setSelectedChannel] = useState<ChatChannel | null>(null);
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [replyTo, setReplyTo] = useState<ChatMessageType | null>(null);
  const [editingMessage, setEditingMessage] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showChannelInfo, setShowChannelInfo] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [showMemberPicker, setShowMemberPicker] = useState(false);
  const [dmSectionOpen, setDmSectionOpen] = useState(true);
  const [channelsSectionOpen, setChannelsSectionOpen] = useState(true);
  const [showCreateChannel, setShowCreateChannel] = useState(false);
  const [newChannelName, setNewChannelName] = useState('');
  const [newChannelMembers, setNewChannelMembers] = useState<Set<string>>(new Set());
  const [createChannelError, setCreateChannelError] = useState<string | null>(null);
  // Only ever set right after creating a channel (or fetched on demand for the
  // creator via /webhook-info) — never present on channels from the list endpoint.
  const [webhookInfoChannelId, setWebhookInfoChannelId] = useState<string | null>(null);
  const [webhookToken, setWebhookToken] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    fetchAllUsers();
    fetchChannels();

    // Poll channels every 15 seconds (reduced from 5s to lower server load)
    const channelPollInterval = setInterval(fetchChannels, 15000);

    return () => clearInterval(channelPollInterval);
  }, []);

  // Best-effort: opt this browser into push notifications so new chat
  // messages are delivered even when this tab isn't open/focused. Silently
  // no-ops if the browser doesn't support push or the user denies the
  // permission prompt — chat still works via polling either way.
  useEffect(() => {
    (async () => {
      try {
        const already = await isSubscribedToPush();
        if (!already) {
          await subscribeToPushNotifications(currentUser.id, currentUser.name, currentUser.email);
        }
      } catch (error) {
        console.error('Error setting up chat push notifications:', error);
      }
    })();
  }, [currentUser.id, currentUser.name, currentUser.email]);

  useEffect(() => {
    if (selectedChannel) {
      fetchMessages();
      setSendError(null);
      setFileError(null);
      // Poll messages every 10 seconds (reduced from 5s to lower server load)
      const interval = setInterval(fetchMessages, 10000);
      return () => clearInterval(interval);
    }
  }, [selectedChannel]);

  // Calculate and report total unread messages
  useEffect(() => {
    if (onUnreadCountChange) {
      const totalUnread = channels.reduce((sum, channel) => sum + (channel.unreadCount || 0), 0);
      onUnreadCountChange(totalUnread);
    }
  }, [channels, onUnreadCountChange]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-grow the composer as multi-line/pasted text (e.g. formatted JSON) is
  // typed in, instead of scrolling within a fixed single line.
  useEffect(() => {
    const el = composerRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [newMessage]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchAllUsers = async () => {
    try {
      const response = await fetch(getApiUrl('api/users'));
      if (!response.ok) {
        console.error('Error fetching users:', await readApiError(response));
        return;
      }
      const users = await response.json();
      if (!Array.isArray(users)) return;
      setAllUsers(users.filter((u: User) => u.id !== currentUser.id));
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchChannels = async () => {
    try {
      const response = await fetch(getApiUrl(`api/chat/channels?userId=${currentUser.id}`));
      if (!response.ok) {
        toast(await readApiError(response, 'Failed to load chat channels'), 'error');
        return;
      }
      const data = await response.json();
      if (Array.isArray(data)) setChannels(data);
    } catch (error) {
      console.error('Error fetching channels:', error);
    }
  };

  const fetchMessages = async () => {
    if (!selectedChannel) return;
    
    try {
      const response = await fetch(getApiUrl(`api/chat/messages?channelId=${selectedChannel._id}&limit=100`));
      if (!response.ok) {
        console.error('Error fetching messages:', await readApiError(response));
        return;
      }
      const data = await response.json();
      if (!Array.isArray(data)) return;
      setMessages(data);
      
      // Mark as read
      const unreadIds = data
        .filter((msg: ChatMessageType) => 
          msg.senderId !== currentUser.id && 
          !msg.readBy.some(r => r.userId === currentUser.id)
        )
        .map((msg: ChatMessageType) => msg._id);
      
      if (unreadIds.length > 0) {
        await markAsRead(unreadIds);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const markAsRead = async (messageIds: string[]) => {
    try {
      await fetch(getApiUrl('api/chat/messages/mark-read'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: currentUser.id,
          userName: currentUser.name,
          messageIds
        })
      });
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const generateChannelId = (otherUserId: string) => {
    const sorted = [currentUser.id, otherUserId].sort();
    return `direct-${sorted[0]}-${sorted[1]}`;
  };

  const startDirectChat = (user: User) => {
    const channelId = generateChannelId(user.id);
    const existingChannel = channels.find(ch => ch._id === channelId);
    
    if (existingChannel) {
      setSelectedChannel(existingChannel);
    } else {
      // Create new channel object
      const newChannel: ChatChannel = {
        _id: channelId,
        channelType: 'direct',
        participants: [
          {
            userId: currentUser.id,
            userName: currentUser.name,
            userEmail: currentUser.email
          },
          {
            userId: user.id,
            userName: user.name,
            userEmail: user.email
          }
        ],
        lastMessage: '',
        lastMessageTime: new Date(),
        lastSenderName: '',
        lastSenderId: ''
      };
      setSelectedChannel(newChannel);
    }
    setShowMemberPicker(false);
  };

  const createChannel = async () => {
    if (!newChannelName.trim()) {
      setCreateChannelError('Channel name is required');
      return;
    }
    if (newChannelMembers.size === 0) {
      setCreateChannelError('Select at least one member');
      return;
    }
    setCreateChannelError(null);

    try {
      const participants = allUsers
        .filter(u => newChannelMembers.has(u.id))
        .map(u => ({ userId: u.id, userName: u.name, userEmail: u.email || '' }));

      const response = await fetch(getApiUrl('api/chat/channels'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newChannelName.trim(),
          participants,
          createdBy: { id: currentUser.id, name: currentUser.name, email: currentUser.email }
        })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to create channel');
      }

      const newChannel = await response.json();
      setChannels(prev => [newChannel, ...prev]);
      setSelectedChannel(newChannel);
      setWebhookInfoChannelId(newChannel._id);
      setWebhookToken(newChannel.webhookToken);
      setShowCreateChannel(false);
      setNewChannelName('');
      setNewChannelMembers(new Set());
    } catch (error) {
      setCreateChannelError(error instanceof Error ? error.message : 'Failed to create channel');
    }
  };

  const fetchWebhookInfo = async (channelId: string) => {
    try {
      const response = await fetch(getApiUrl(`api/chat/channels/${channelId}/webhook-info?userId=${currentUser.id}`));
      if (!response.ok) return;
      const data = await response.json();
      setWebhookInfoChannelId(data.channelId);
      setWebhookToken(data.webhookToken);
    } catch (error) {
      console.error('Error fetching webhook info:', error);
    }
  };

  // Core send/edit logic, split out from the form submit handler so a failed
  // send can be retried (via the error banner's Retry button) without the
  // user having to retype anything — the draft is preserved on failure.
  const performSend = async () => {
    if (!newMessage.trim() || !selectedChannel) return;
    setSendError(null);

    try {
      if (editingMessage) {
        // Re-encrypt the edited text, same as a new message — otherwise
        // edited messages would be stored as plain text while everything
        // else in the channel is encrypted.
        const encryptedEdit = encryptMessage(newMessage, selectedChannel._id);
        const response = await fetch(getApiUrl(`api/chat/messages/${editingMessage}`), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: encryptedEdit,
            senderId: currentUser.id
          })
        });

        if (!response.ok) throw new Error('Failed to save changes');

        const updated = await response.json();
        setMessages(messages.map(msg => msg._id === updated._id ? updated : msg));
        setEditingMessage(null);
      } else {
        // Encrypt the message before sending using channel-based key
        const encryptedMessage = encryptMessage(newMessage, selectedChannel._id);

        const response = await fetch(getApiUrl('api/chat/messages'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            channelId: selectedChannel._id,
            channelType: selectedChannel.channelType,
            participants: selectedChannel.participants,
            message: encryptedMessage,
            isEncrypted: true,
            senderId: currentUser.id,
            senderName: currentUser.name,
            senderEmail: currentUser.email,
            senderRole: currentUser.role,
            replyTo: replyTo ? {
              messageId: replyTo._id,
              senderName: replyTo.senderName,
              message: decryptMessage(replyTo.message, selectedChannel._id) // Decrypt for preview
            } : undefined
          })
        });

        if (!response.ok) throw new Error('Failed to send message');

        const sentMessage = await response.json();
        setMessages(prev => [...prev, sentMessage]);
        // Refetch messages to ensure sync
        await fetchMessages();
        // Refetch channels to update last message
        await fetchChannels();
      }

      setNewMessage('');
      setReplyTo(null);
    } catch (error) {
      console.error('Error sending message:', error);
      // Deliberately do NOT clear newMessage/replyTo/editingMessage here —
      // that's what let the draft silently vanish on a failed send before.
      setSendError(
        editingMessage
          ? 'Failed to save changes. Your text is still here — tap Retry.'
          : 'Failed to send. Your message is still here — tap Retry.'
      );
    }
  };

  const sendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    performSend();
  };

  const copyMessage = async (messageId: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId((current) => (current === messageId ? null : current)), 1500);
    } catch (error) {
      console.error('Error copying message:', error);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedChannel) return;

    setIsLoading(true);
    setFileError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('channelId', selectedChannel._id);
      formData.append('channelType', selectedChannel.channelType);
      formData.append('participants', JSON.stringify(selectedChannel.participants));
      formData.append('senderId', currentUser.id);
      formData.append('senderName', currentUser.name);
      formData.append('senderEmail', currentUser.email || '');
      formData.append('senderRole', currentUser.role);

      const response = await fetch(getApiUrl('api/chat/messages/upload'), {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const uploadedMessage = await response.json();
        setMessages([...messages, uploadedMessage]);
        await fetchChannels();
      } else {
        // Previously failed completely silently — the file input would just
        // reset with no feedback at all, leaving no sign the upload never sent.
        const data = await response.json().catch(() => ({}));
        setFileError(data.error || 'Failed to upload file.');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setFileError('Failed to upload file.');
    } finally {
      setIsLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const deleteMessage = async (messageId: string) => {
    if (!confirm('Delete this message?')) return;

    try {
      const response = await fetch(
        getApiUrl(`api/chat/messages/${messageId}?senderId=${currentUser.id}`),
        { method: 'DELETE' }
      );

      if (response.ok) {
        setMessages(messages.filter(msg => msg._id !== messageId));
      }
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  const clearChatHistory = async () => {
    if (!selectedChannel) return;
    if (!confirm('Clear all messages in this conversation? This cannot be undone.')) return;

    try {
      const response = await fetch(
        getApiUrl(`api/chat/channels/${selectedChannel._id}/messages?userId=${currentUser.id}`),
        { method: 'DELETE' }
      );

      if (response.ok) {
        setMessages([]);
        setShowChannelInfo(false);
        await fetchChannels();
      } else {
        const data = await response.json().catch(() => ({}));
        toast(data.error || 'Failed to clear chat history.');
      }
    } catch (error) {
      console.error('Error clearing chat history:', error);
      toast('Failed to clear chat history.');
    }
  };

  const addReaction = async (messageId: string, emoji: string) => {
    try {
      const response = await fetch(getApiUrl(`api/chat/messages/${messageId}/react`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: currentUser.id,
          userName: currentUser.name,
          emoji
        })
      });

      if (response.ok) {
        const updated = await response.json();
        setMessages(messages.map(msg => msg._id === updated._id ? updated : msg));
      }
    } catch (error) {
      console.error('Error adding reaction:', error);
    }
  };

  const getOtherParticipant = (channel: ChatChannel) => {
    return channel.participants.find(p => p.userId !== currentUser.id);
  };

  const getChannelDisplayName = (channel: ChatChannel) => {
    if (channel.channelType === 'direct') {
      const other = getOtherParticipant(channel);
      return other?.userName || 'Unknown User';
    }
    return channel.name || 'Group Chat';
  };

  const formatDateDivider = (date: Date | string) => {
    const d = new Date(date);
    if (isToday(d)) return `Today, ${format(d, 'MMMM d')}`;
    if (isYesterday(d)) return `Yesterday, ${format(d, 'MMMM d')}`;
    return format(d, 'MMMM d, yyyy');
  };

  // Lightweight, purely visual @mention highlighting — matches single-token
  // handles (e.g. "@Momon") the way the message was actually typed. There's
  // no real mention system behind this (no autocomplete, no notification-on-
  // mention), so this only styles text that already looks like a mention
  // rather than claiming to tag/notify anyone.
  const renderMessageText = (text: string, isOwnMessage: boolean) => {
    const parts = text.split(/(@\w+)/g);
    return parts.map((part, i) =>
      /^@\w+$/.test(part) ? (
        <span
          key={i}
          className={`font-semibold ${isOwnMessage ? 'text-blue-100 underline' : 'text-blue-600'}`}
        >
          {part}
        </span>
      ) : (
        <React.Fragment key={i}>{part}</React.Fragment>
      )
    );
  };

  const filteredUsers = searchQuery
    ? allUsers.filter(u =>
        // Some real accounts have no email set in Keycloak — calling
        // .toLowerCase() on that directly crashed the whole chat panel
        // (caught by the ErrorBoundary as "Application Error") the moment
        // anyone typed into search, since our own seed/test accounts all
        // happened to have emails and never exposed this.
        (u.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (u.email || '').toLowerCase().includes(searchQuery.toLowerCase())
      )
    : allUsers;

  // The search box is labeled "Search conversations..." but only ever
  // filtered the "All Team Members" list below it — the actual conversation
  // list above never reacted to it at all, so searching for someone you
  // already had a conversation with visibly did nothing.
  const filteredChannels = searchQuery
    ? channels.filter(ch => getChannelDisplayName(ch).toLowerCase().includes(searchQuery.toLowerCase()))
    : channels;

  const filteredDirectChannels = filteredChannels.filter(ch => ch.channelType !== 'group');
  const filteredGroupChannels = filteredChannels.filter(ch => ch.channelType === 'group');

  return (
    <div className="fixed inset-0 bg-gray-50 flex flex-col z-40 ml-64">
      {/* Top Header Bar - Professional MNC Style */}
      <div className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center shadow-md">
              <MessageCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Secure Chat</h1>
              <div className="flex items-center gap-1 text-xs text-green-600">
                <Shield className="w-3 h-3" />
                <span>End-to-end encrypted</span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={() => setShowProfileModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 hover:border-gray-300 transition-all cursor-pointer"
          >
            <Avatar name={currentUser.name} userId={currentUser.id} profilePicture={currentUser.profilePicture} size="sm" />
            <div className="text-left">
              <p className="text-sm font-medium text-gray-900">{currentUser.name}</p>
              <p className="text-xs text-gray-500 capitalize">{currentUser.role}</p>
            </div>
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Slack-style dark workspace rail */}
        <div className="w-72 bg-[#191c2b] flex flex-col border-r border-black/30">
          {/* Workspace Header */}
          <div className="px-4 py-4 flex items-center justify-between border-b border-white/10">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-8 h-8 bg-blue-600 rounded-md flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-4 h-4 text-white" />
              </div>
              <h2 className="text-white font-bold text-base truncate">Team Chat</h2>
            </div>
            <button
              onClick={() => setShowMemberPicker((v) => !v)}
              className={`p-1.5 rounded-md transition-colors flex-shrink-0 ${showMemberPicker ? 'bg-white/15 text-white' : 'text-gray-300 hover:bg-white/10 hover:text-white'}`}
              title="New message"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>

          {/* Search */}
          <div className="px-3 py-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 bg-white/10 border border-transparent rounded-md text-sm text-white placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:bg-white/15 transition-colors"
              />
            </div>
          </div>

          {/* Channels/Users List */}
          <div className="flex-1 overflow-y-auto">
            {/* Group Channels */}
            <div className="px-3 py-1.5 flex items-center justify-between">
              <button
                onClick={() => setChannelsSectionOpen((v) => !v)}
                className="flex items-center gap-1.5 text-gray-400 hover:text-gray-200 transition-colors"
              >
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${channelsSectionOpen ? '' : '-rotate-90'}`} />
                <span className="text-xs font-bold uppercase tracking-wider">Channels</span>
              </button>
              <button
                onClick={() => setShowCreateChannel(true)}
                className="text-gray-400 hover:text-white transition-colors"
                title="Create channel"
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>

            {channelsSectionOpen && filteredGroupChannels.map((channel) => {
              const isActive = selectedChannel?._id === channel._id;
              const hasUnread = Boolean(channel.unreadCount && channel.unreadCount > 0);
              return (
                <button
                  key={channel._id}
                  onClick={() => setSelectedChannel(channel)}
                  className={`w-full mx-2 my-0.5 px-2.5 py-2 rounded-md transition-colors text-left flex items-center gap-3 relative ${
                    isActive ? 'bg-[#1164A3]' : 'hover:bg-white/10'
                  }`}
                  style={{ width: 'calc(100% - 1rem)' }}
                >
                  <Hash className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-blue-100' : 'text-gray-400'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-0.5">
                      <p className={`truncate text-sm ${hasUnread && !isActive ? 'font-bold text-white' : isActive ? 'text-white font-medium' : 'text-gray-300'}`}>
                        {getChannelDisplayName(channel)}
                      </p>
                      {hasUnread && channel.unreadCount && (
                        <span className="bg-red-500 text-white text-[10px] rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1.5 font-bold shadow-sm flex-shrink-0 ml-2">
                          {channel.unreadCount > 99 ? '99+' : channel.unreadCount}
                        </span>
                      )}
                    </div>
                    {channel.lastMessage && (
                      <p className={`text-xs truncate ${isActive ? 'text-blue-100' : hasUnread ? 'text-gray-300 font-medium' : 'text-gray-500'}`}>
                        {channel.lastSenderName ? `${channel.lastSenderName.split(' ')[0]}: ` : ''}
                        {decryptMessage(channel.lastMessage, channel._id).substring(0, 30)}
                      </p>
                    )}
                  </div>
                </button>
              );
            })}

            {/* Direct Messages */}
            <div className="px-3 py-1.5 mt-2 flex items-center justify-between">
              <button
                onClick={() => setDmSectionOpen((v) => !v)}
                className="flex items-center gap-1.5 text-gray-400 hover:text-gray-200 transition-colors"
              >
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${dmSectionOpen ? '' : '-rotate-90'}`} />
                <span className="text-xs font-bold uppercase tracking-wider">Direct Messages</span>
              </button>
              {filteredDirectChannels.length > 0 && (
                <span className="text-[10px] font-medium text-gray-500">{filteredDirectChannels.length}</span>
              )}
            </div>

            {dmSectionOpen && filteredDirectChannels.map((channel) => {
              const otherUser = getOtherParticipant(channel);
              const isActive = selectedChannel?._id === channel._id;
              // Coerced to a real boolean — `channel.unreadCount && ...` evaluates to
              // the number 0 (not false) when there are no unread messages, and React
              // renders that literal "0" as text wherever this gets used in `{hasUnread && ...}`.
              const hasUnread = Boolean(channel.unreadCount && channel.unreadCount > 0);
              return (
                <button
                  key={channel._id}
                  onClick={() => setSelectedChannel(channel)}
                  className={`w-full mx-2 my-0.5 px-2.5 py-2 rounded-md transition-colors text-left flex items-center gap-3 relative ${
                    isActive ? 'bg-[#1164A3]' : 'hover:bg-white/10'
                  }`}
                  style={{ width: 'calc(100% - 1rem)' }}
                >
                  <div className="relative flex-shrink-0">
                    <Avatar name={otherUser?.userName} userId={otherUser?.userId} size="md" />
                    <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-[#191c2b]"></div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-0.5">
                      <p className={`truncate text-sm ${hasUnread && !isActive ? 'font-bold text-white' : isActive ? 'text-white font-medium' : 'text-gray-300'}`}>
                        {getChannelDisplayName(channel)}
                      </p>
                      <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                        {channel.lastMessageTime && (
                          <span className={`text-[11px] ${isActive ? 'text-blue-100' : hasUnread ? 'text-gray-200' : 'text-gray-500'}`}>
                            {format(new Date(channel.lastMessageTime), 'hh:mm a')}
                          </span>
                        )}
                        {hasUnread && channel.unreadCount && (
                          <span className="bg-red-500 text-white text-[10px] rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1.5 font-bold shadow-sm">
                            {channel.unreadCount > 99 ? '99+' : channel.unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                    {channel.lastMessage && (
                      <p className={`text-xs truncate ${isActive ? 'text-blue-100' : hasUnread ? 'text-gray-300 font-medium' : 'text-gray-500'}`}>
                        <Lock className="w-3 h-3 inline mr-1" />
                        {decryptMessage(channel.lastMessage, channel._id).substring(0, 30)}...
                      </p>
                    )}
                  </div>
                </button>
              );
            })}

            {/* New message picker - shown on demand via the "+" button instead
                of always rendering the entire team roster in the sidebar. */}
            {showMemberPicker && (
              <div className="mt-2 pt-2 border-t border-white/10">
                <div className="px-4 py-1.5">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">All Team Members</p>
                </div>
                {filteredUsers.map((user) => {
                  const existingChannel = channels.find(ch =>
                    ch.participants.some(p => p.userId === user.id)
                  );
                  const isActive = selectedChannel && selectedChannel.participants.some(p => p.userId === user.id);

                  return (
                    <button
                      key={user.id}
                      onClick={() => startDirectChat(user)}
                      className={`w-full mx-2 my-0.5 px-2.5 py-2 rounded-md transition-colors text-left flex items-center gap-3 ${
                        isActive ? 'bg-[#1164A3]' : 'hover:bg-white/10'
                      }`}
                      style={{ width: 'calc(100% - 1rem)' }}
                    >
                      <div className="relative flex-shrink-0">
                        <Avatar name={user.name} userId={user.id} size="md" />
                        <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-[#191c2b]"></div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium text-sm truncate ${isActive ? 'text-white' : 'text-gray-300'}`}>{user.name}</p>
                        <p className="text-xs text-gray-500 capitalize">{user.role}</p>
                      </div>
                      {!existingChannel && (
                        <MessageCircle className="w-4 h-4 text-gray-500 flex-shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Current user footer, Slack-style */}
          <button
            onClick={() => setShowProfileModal(true)}
            className="px-3 py-3 border-t border-white/10 flex items-center gap-2.5 hover:bg-white/5 transition-colors flex-shrink-0"
          >
            <div className="relative flex-shrink-0">
              <Avatar name={currentUser.name} userId={currentUser.id} profilePicture={currentUser.profilePicture} size="sm" />
              <div className="absolute bottom-0 right-0 w-2 h-2 bg-green-500 rounded-full border-2 border-[#191c2b]"></div>
            </div>
            <div className="flex-1 min-w-0 text-left">
              <p className="text-sm font-medium text-white truncate">{currentUser.name}</p>
              <p className="text-xs text-gray-400 capitalize truncate">{currentUser.role}</p>
            </div>
          </button>
        </div>

        {/* Right Side - Chat Area - Professional MNC Style */}
      {selectedChannel ? (
        <div className="flex-1 flex flex-col bg-white">
          {/* Chat Header - Enhanced with Actions */}
          <div className="px-6 py-4 border-b border-gray-200 bg-white shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {selectedChannel.channelType === 'group' ? (
                  <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <Hash className="w-6 h-6 text-gray-500" />
                  </div>
                ) : (
                  <div className="relative">
                    <Avatar name={getOtherParticipant(selectedChannel)?.userName} userId={getOtherParticipant(selectedChannel)?.userId} size="lg" />
                    <div className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-white"></div>
                  </div>
                )}
                <div>
                  <h2 className="font-semibold text-gray-900 text-lg">{getChannelDisplayName(selectedChannel)}</h2>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    {selectedChannel.channelType === 'group' ? (
                      <span>{selectedChannel.participants.length} members</span>
                    ) : (
                      <>
                        <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                        <span>Active now</span>
                      </>
                    )}
                    <span className="mx-2">•</span>
                    <Lock className="w-3 h-3" />
                    <span className="text-xs">Encrypted</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {selectedChannel.channelType !== 'group' && (
                  <>
                    <button
                      onClick={() => toast('Voice call feature coming soon!')}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Voice Call"
                    >
                      <Phone className="w-5 h-5 text-gray-600" />
                    </button>
                    <button
                      onClick={() => toast('Video call feature coming soon!')}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Video Call"
                    >
                      <Video className="w-5 h-5 text-gray-600" />
                    </button>
                  </>
                )}
                <button
                  onClick={() => setShowChannelInfo(!showChannelInfo)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Info"
                >
                  <Info className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            </div>
          </div>

          {/* Messages Area - Professional Styling */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gradient-to-b from-gray-50 to-white">
            {messages.map((msg, index) => {
              const isOwnMessage = msg.senderId === currentUser.id;
              const isRead = msg.readBy.some(r => r.userId === currentUser.id);
              // Check if OTHER user has read the message (for double tick)
              const isReadByOthers = msg.readBy.some(r => r.userId !== currentUser.id && r.userId !== msg.senderId);
              const isNewMessage = !isOwnMessage && !isRead;
              const decryptedMessage = decryptMessage(msg.message, selectedChannel._id);
              const prevMsg = messages[index - 1];
              const showDateDivider = !prevMsg || !isSameDay(new Date(msg.createdAt), new Date(prevMsg.createdAt));

              return (
                <React.Fragment key={msg._id}>
                {showDateDivider && (
                  <div className="flex items-center justify-center my-3">
                    <span className="px-3 py-1 bg-gray-200/70 text-gray-600 text-xs font-semibold rounded-full">
                      {formatDateDivider(msg.createdAt)}
                    </span>
                  </div>
                )}
                <div
                  className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} group ${
                    isNewMessage ? 'animate-slideIn' : ''
                  }`}
                >
                  <div className={`max-w-[70%] ${isOwnMessage ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
                    {!isOwnMessage && (
                      <div className="flex items-center gap-2 px-3">
                        <Avatar name={msg.senderName} userId={msg.senderId} size="xs" />
                        <span className={`text-xs font-medium ${isNewMessage ? 'text-gray-900 font-semibold' : 'text-gray-700'}`}>
                          {msg.senderName}
                        </span>
                      </div>
                    )}
                    
                    {msg.replyTo && (
                      <div className="text-xs bg-gray-100 px-3 py-2 rounded-lg border-l-3 border-blue-500 mb-1">
                        <span className="font-semibold text-gray-700">{msg.replyTo.senderName}</span>
                        <p className="text-gray-600 truncate mt-0.5">
                          {decryptMessage(msg.replyTo.message, selectedChannel._id)}
                        </p>
                      </div>
                    )}

                    <div
                      className={`rounded-2xl px-4 py-2.5 shadow-sm ${
                        isOwnMessage
                          ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white'
                          : 'bg-white text-gray-900 border border-gray-200'
                      } relative group-hover:shadow-md transition-all`}
                    >
                      {msg.messageType === 'image' && msg.fileUrl && (
                        <img
                          src={getApiUrl(msg.fileUrl)}
                          alt="Uploaded image"
                          className="max-w-full rounded-lg mb-2 cursor-pointer border border-black/5 shadow-sm"
                          onClick={() => msg.fileUrl && window.open(getApiUrl(msg.fileUrl), '_blank')}
                        />
                      )}

                      {msg.messageType === 'file' && msg.fileUrl && (
                        <a
                          href={getApiUrl(msg.fileUrl)}
                          download={msg.fileName}
                          className={`flex items-center gap-3 mb-2 px-3 py-2.5 rounded-xl transition-colors ${
                            isOwnMessage ? 'bg-white/15 hover:bg-white/25' : 'bg-gray-50 hover:bg-gray-100 border border-gray-200'
                          }`}
                        >
                          <span className={`p-2 rounded-lg flex-shrink-0 ${isOwnMessage ? 'bg-white/20' : 'bg-blue-100'}`}>
                            <Paperclip className={`w-4 h-4 ${isOwnMessage ? 'text-white' : 'text-blue-600'}`} />
                          </span>
                          <span className="text-sm font-medium truncate flex-1">{msg.fileName}</span>
                          <Download className="w-4 h-4 flex-shrink-0" />
                        </a>
                      )}

                      <p className="text-sm break-words leading-relaxed whitespace-pre-wrap">{renderMessageText(decryptedMessage, isOwnMessage)}</p>
                      
                      {msg.reactions && msg.reactions.length > 0 && (
                        <div className="flex gap-1 mt-2">
                          {Array.from(new Set(msg.reactions.map(r => r.emoji))).map((emoji: string) => {
                            const count = msg.reactions.filter(r => r.emoji === emoji).length;
                            return (
                              <span
                                key={emoji}
                                className="text-xs bg-gray-100 px-2 py-0.5 rounded-full cursor-pointer hover:bg-gray-200"
                                onClick={() => addReaction(msg._id, emoji)}
                              >
                                {emoji} {count}
                              </span>
                            );
                          })}
                        </div>
                      )}

                      {isOwnMessage && (
                        <div className="absolute -top-2 -left-2 hidden group-hover:flex gap-1 bg-white rounded shadow-lg p-1">
                          <button
                            onClick={() => copyMessage(msg._id, decryptedMessage)}
                            className="p-1 hover:bg-gray-100 rounded"
                            title="Copy"
                          >
                            {copiedMessageId === msg._id ? (
                              <Check className="w-3 h-3 text-emerald-600" />
                            ) : (
                              <Copy className="w-3 h-3 text-gray-600" />
                            )}
                          </button>
                          <button
                            onClick={() => {
                              setEditingMessage(msg._id);
                              setNewMessage(decryptedMessage);
                            }}
                            className="p-1 hover:bg-gray-100 rounded"
                            title="Edit"
                          >
                            <Edit className="w-3 h-3 text-gray-600" />
                          </button>
                          <button
                            onClick={() => deleteMessage(msg._id)}
                            className="p-1 hover:bg-gray-100 rounded"
                            title="Delete"
                          >
                            <Trash2 className="w-3 h-3 text-red-600" />
                          </button>
                        </div>
                      )}
                      
                      {!isOwnMessage && (
                        <div className="absolute -top-2 -right-2 hidden group-hover:flex gap-1 bg-white rounded shadow-lg p-1">
                          <button
                            onClick={() => copyMessage(msg._id, decryptedMessage)}
                            className="p-1 hover:bg-gray-100 rounded"
                            title="Copy"
                          >
                            {copiedMessageId === msg._id ? (
                              <Check className="w-3 h-3 text-emerald-600" />
                            ) : (
                              <Copy className="w-3 h-3 text-gray-600" />
                            )}
                          </button>
                          <button
                            onClick={() => setReplyTo(msg)}
                            className="p-1 hover:bg-gray-100 rounded"
                            title="Reply"
                          >
                            <Reply className="w-3 h-3 text-gray-600" />
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2 px-3 mt-1">
                      <span className={`text-xs ${isOwnMessage ? 'text-blue-100' : 'text-gray-500'}`}>
                        {format(new Date(msg.createdAt), 'HH:mm')}
                      </span>
                      {msg.isEdited && (
                        <span className={`text-xs ${isOwnMessage ? 'text-blue-200' : 'text-gray-400'}`}>(edited)</span>
                      )}
                      {isOwnMessage && (
                        <span className={`text-xs flex items-center ${isReadByOthers ? 'text-blue-400' : 'text-blue-200'}`}>
                          {isReadByOthers ? <CheckCheck className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5" />}
                        </span>
                      )}
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-2">
                        <button
                          onClick={() => addReaction(msg._id, '👍')}
                          className="text-sm hover:scale-125 transition-transform bg-white border border-gray-200 rounded-full w-6 h-6 flex items-center justify-center shadow-sm"
                          title="Like"
                        >
                          👍
                        </button>
                        <button
                          onClick={() => addReaction(msg._id, '❤️')}
                          className="text-sm hover:scale-125 transition-transform bg-white border border-gray-200 rounded-full w-6 h-6 flex items-center justify-center shadow-sm"
                          title="Love"
                        >
                          ❤️
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                </React.Fragment>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* Reply/Edit Preview */}
          {(replyTo || editingMessage) && (
            <div className={`px-4 py-2 ${replyTo ? 'bg-blue-50 border-blue-200' : 'bg-yellow-50 border-yellow-200'} border-t flex items-center justify-between`}>
              <div className="flex-1">
                <p className={`text-xs font-medium ${replyTo ? 'text-blue-900' : 'text-yellow-900'}`}>
                  {replyTo ? `Replying to ${replyTo.senderName}` : 'Editing message'}
                </p>
                {replyTo && selectedChannel && (
                  <p className="text-xs text-blue-700 truncate">
                    {decryptMessage(replyTo.message, selectedChannel._id)}
                  </p>
                )}
              </div>
              <button
                onClick={() => {
                  setReplyTo(null);
                  setEditingMessage(null);
                  setNewMessage('');
                  setSendError(null);
                }}
                className={`p-1 ${replyTo ? 'hover:bg-blue-100' : 'hover:bg-yellow-100'} rounded`}
              >
                <X className={`w-4 h-4 ${replyTo ? 'text-blue-600' : 'text-yellow-600'}`} />
              </button>
            </div>
          )}

          {/* Send/Edit Error - message text is preserved, retry re-sends it as-is */}
          {sendError && (
            <div className="px-4 py-2 bg-red-50 border-t border-red-200 flex items-center justify-between gap-3">
              <span className="text-xs text-red-700">{sendError}</span>
              <button
                onClick={performSend}
                className="flex items-center gap-1 text-xs font-semibold text-red-700 hover:text-red-900 shrink-0"
              >
                <RefreshCw className="w-3 h-3" />
                Retry
              </button>
            </div>
          )}

          {/* File Upload Error — no retry button here since the file input
              already cleared; re-attaching the file is the natural retry. */}
          {fileError && (
            <div className="px-4 py-2 bg-red-50 border-t border-red-200 flex items-center justify-between gap-3">
              <span className="text-xs text-red-700">{fileError}</span>
              <button
                onClick={() => setFileError(null)}
                className="text-xs font-semibold text-red-700 hover:text-red-900 shrink-0"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Input Area - Professional MNC Style */}
          <form onSubmit={sendMessage} className="p-4 border-t border-gray-200 bg-white">
            <div className="flex items-end gap-3">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                className="hidden"
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar,.7z,.tar,.gz,.pem,.key,.crt,.cer,.p12,.pfx,.jks,.json,.xml,.csv,.log,.sql,.sh,.bat,.ps1,.yaml,.yml,.md,.ppt,.pptx"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="p-2.5 hover:bg-gray-100 rounded-lg transition-all"
                  disabled={isLoading}
                  title="Attach file"
                >
                  <Paperclip className="w-5 h-5 text-gray-600" />
                </button>
              </div>
              <div className="flex-1 relative">
                <textarea
                  ref={composerRef}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => {
                    // Enter sends; Shift+Enter (or pasting multi-line text,
                    // e.g. formatted JSON) inserts a real line break instead
                    // of being flattened into one run-on paragraph.
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      performSend();
                    }
                  }}
                  placeholder={editingMessage ? "Edit message..." : "Type your message... (Shift+Enter for a new line)"}
                  rows={1}
                  className="w-full px-4 py-3 pr-24 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all placeholder-gray-400 resize-none leading-relaxed"
                  style={{ maxHeight: '160px' }}
                  disabled={isLoading}
                />
                <div className="absolute right-3 top-3 flex items-center gap-1 text-xs text-gray-400">
                  <Lock className="w-3 h-3" />
                  <span>Encrypted</span>
                </div>
              </div>
              <button
                type="submit"
                disabled={!newMessage.trim() || isLoading}
                className="p-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
                title="Send message"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
            <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
              <Shield className="w-3 h-3 text-green-600" />
              <span>All messages are end-to-end encrypted</span>
            </div>
          </form>
        </div>
      ) : (
        /* No Channel Selected - Professional Empty State */
        <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-gray-50 to-white">
          <div className="text-center max-w-md px-8">
            <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mx-auto mb-6">
              <MessageCircle className="w-10 h-10 text-blue-600" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-3">Welcome to Secure Chat</h3>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Select a team member from the sidebar to start a secure, encrypted conversation.
            </p>
            <div className="flex items-center justify-center gap-8 text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-green-600" />
                <span>End-to-end encrypted</span>
              </div>
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-blue-600" />
                <span>AES-256 secure</span>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>

      {/* Profile Modal */}
      {showProfileModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-900">Your Profile</h3>
              <button
                onClick={() => setShowProfileModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            
            <div className="flex flex-col items-center mb-6">
              <Avatar name={currentUser.name} userId={currentUser.id} profilePicture={currentUser.profilePicture} size="xl" className="mb-4" />
              <h4 className="text-xl font-semibold text-gray-900 mb-1">{currentUser.name}</h4>
              <p className="text-sm text-gray-600 mb-1">{currentUser.email}</p>
              <p className="text-xs text-gray-500 uppercase tracking-wide bg-gray-100 px-3 py-1 rounded-full">
                {currentUser.role}
              </p>
            </div>

            <div className="border-t border-gray-200 pt-4 space-y-3">
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-gray-600">Status</span>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium text-green-600">Active</span>
                </div>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-gray-600">Total Conversations</span>
                <span className="text-sm font-semibold text-gray-900">{channels.length}</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-gray-600">Encryption</span>
                <div className="flex items-center gap-1 text-green-600">
                  <Lock className="w-3 h-3" />
                  <span className="text-xs font-medium">AES-256</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Channel Info Modal */}
      {showChannelInfo && selectedChannel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-900">Chat Information</h3>
              <button
                onClick={() => setShowChannelInfo(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            
            {selectedChannel.channelType === 'group' ? (
              <div className="flex flex-col items-center mb-6">
                <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                  <Hash className="w-9 h-9 text-gray-500" />
                </div>
                <h4 className="text-xl font-semibold text-gray-900 mb-1">{getChannelDisplayName(selectedChannel)}</h4>
                <p className="text-sm text-gray-600">{selectedChannel.participants.length} members</p>
              </div>
            ) : (
              (() => {
                const otherUser = getOtherParticipant(selectedChannel);
                return (
                  <div className="flex flex-col items-center mb-6">
                    <Avatar name={otherUser?.userName} userId={otherUser?.userId} size="xl" className="mb-4" />
                    <h4 className="text-xl font-semibold text-gray-900 mb-1">{getChannelDisplayName(selectedChannel)}</h4>
                    <p className="text-sm text-gray-600 mb-2">{otherUser?.userEmail || 'N/A'}</p>
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-green-600">Active now</span>
                    </div>
                  </div>
                );
              })()
            )}

            {selectedChannel.channelType === 'group' && (
              <div className="border-t border-gray-200 pt-4 pb-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Members</p>
                <div className="max-h-40 overflow-y-auto space-y-2">
                  {selectedChannel.participants.map((p) => (
                    <div key={p.userId} className="flex items-center gap-2">
                      <Avatar name={p.userName} userId={p.userId} size="xs" />
                      <span className="text-sm text-gray-800">{p.userName}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="border-t border-gray-200 pt-4 space-y-3">
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-gray-600">Messages</span>
                <span className="text-sm font-semibold text-gray-900">{messages.length}</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-gray-600">Encryption</span>
                <div className="flex items-center gap-1 text-green-600">
                  <Shield className="w-3 h-3" />
                  <span className="text-xs font-medium">End-to-end encrypted</span>
                </div>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-gray-600">Channel Type</span>
                <span className="text-sm font-medium text-gray-900">
                  {selectedChannel.channelType === 'group' ? 'Group Channel' : 'Direct Message'}
                </span>
              </div>
            </div>

            {selectedChannel.channelType === 'group' && (
              <div className="border-t border-gray-200 pt-4 mt-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <Webhook className="w-3.5 h-3.5" /> Jenkins / Webhook Notifications
                </p>
                {webhookInfoChannelId === selectedChannel._id && webhookToken ? (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">
                      One fixed URL — configure it once in Jenkins; every job sends its own channelId + token in the body below to route to the right channel.
                    </p>
                    <code className="block text-[11px] text-gray-800 bg-white border border-gray-200 rounded px-2 py-1.5 break-all mb-2">
                      {getApiUrl('api/chat/webhooks/jenkins')}
                    </code>
                    <button
                      onClick={() => navigator.clipboard.writeText(getApiUrl('api/chat/webhooks/jenkins'))}
                      className="text-xs font-semibold text-blue-600 hover:text-blue-800 mb-3"
                    >
                      Copy URL
                    </button>

                    <p className="text-xs text-gray-500 mb-1">JSON body for this channel:</p>
                    <code className="block text-[11px] text-gray-800 bg-white border border-gray-200 rounded px-2 py-1.5 break-all whitespace-pre-wrap mb-2">
                      {JSON.stringify({ channelId: selectedChannel._id, token: webhookToken, job: '...', buildNumber: 12, status: 'SUCCESS', url: '...' }, null, 2)}
                    </code>
                    <button
                      onClick={() => navigator.clipboard.writeText(JSON.stringify({ channelId: selectedChannel._id, token: webhookToken, job: '...', buildNumber: 12, status: 'SUCCESS', url: '...' }, null, 2))}
                      className="text-xs font-semibold text-blue-600 hover:text-blue-800"
                    >
                      Copy JSON body
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => fetchWebhookInfo(selectedChannel._id)}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Show webhook URL
                  </button>
                )}
              </div>
            )}

            <div className="mt-6 pt-4 border-t border-gray-200">
              <button
                onClick={clearChatHistory}
                className="w-full py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors font-medium"
              >
                Clear Chat History
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Channel Modal */}
      {showCreateChannel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-900">Create Channel</h3>
              <button
                onClick={() => { setShowCreateChannel(false); setCreateChannelError(null); }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            <label className="block text-sm font-medium text-gray-700 mb-1">Channel name</label>
            <input
              type="text"
              value={newChannelName}
              onChange={(e) => setNewChannelName(e.target.value)}
              placeholder="e.g. CI Builds, Student Batch 2026"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
            />

            <label className="block text-sm font-medium text-gray-700 mb-2">Members</label>
            <div className="max-h-56 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100 mb-2">
              {allUsers.map((user) => {
                const checked = newChannelMembers.has(user.id);
                return (
                  <label key={user.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        setNewChannelMembers((prev) => {
                          const next = new Set(prev);
                          if (next.has(user.id)) next.delete(user.id);
                          else next.add(user.id);
                          return next;
                        });
                      }}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <Avatar name={user.name} userId={user.id} size="xs" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900 truncate">{user.name}</p>
                      <p className="text-xs text-gray-500 capitalize">{user.role}</p>
                    </div>
                  </label>
                );
              })}
            </div>
            <p className="text-xs text-gray-500 mb-4">{newChannelMembers.size} member(s) selected</p>

            {createChannelError && (
              <p className="text-sm text-red-600 mb-3">{createChannelError}</p>
            )}

            <button
              onClick={createChannel}
              className="w-full py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Create Channel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatWidget;
